import { nanoid } from "nanoid"
import Logger from "@/utils/Logger"

/** Font folder name -> SRI attribute value, as published by `jobshop/ui/bratenfond`. */
export type FontIntegrityManifest = Record<string, string>

/**
 * How long a fetched manifest may be served, in seconds.
 *
 * Deliberately far below `dataCacheTtl`, which the content caches use. The hashes here
 * describe files owned by another repository: the moment bratenfond deploys a changed
 * `style.css`, the CDN serves the new file while a cached manifest still carries the old
 * hash — and the browser then blocks the stylesheet, because a mismatch fails closed. The
 * TTL is exactly that window of missing fonts, so it stays short. The manifest is 13 KB of
 * JSON, which makes the refresh cheap.
 */
const MANIFEST_MAX_AGE = 300

/**
 * Resolves the Subresource Integrity hashes for the custom-font stylesheets (JET-2734).
 *
 * The hashes cannot be computed here: the stylesheets live in `jobshop/ui/bratenfond` and
 * are deployed by that repository's own pipeline, independently of this build, and their
 * file names carry no content hash. That pipeline therefore publishes them as
 * `sri-manifest.json` next to the fonts, and this fetches it.
 *
 * Gated by the per-tenant `isFontSriEnabled` flag, and returns an empty manifest whenever
 * anything is missing — flag off, no font CDN configured, manifest unreachable. An empty
 * manifest means no `integrity` attribute rather than a broken page, so deploying this
 * repository before bratenfond publishes a manifest costs nothing.
 *
 * A manifest that is present but *stale* is the opposite case and fails closed, since the
 * browser blocks a stylesheet whose hash no longer matches. `MANIFEST_MAX_AGE` bounds it.
 */
export async function useFontIntegrity(): Promise<FontIntegrityManifest> {
  // Resolved once and kept in Nuxt state, so the value the server rendered the integrity
  // attributes from is the one the client hydrates with. Re-resolving during hydration
  // could yield a different manifest and would flip the attributes useHead renders.
  const manifest = useState<FontIntegrityManifest | null>("fontIntegrityManifest", () => null)
  if (manifest.value !== null)
    return manifest.value

  const { public: { tcFontCdnURL }, dataCacheStaleTtl } = useRuntimeConfig()
  const enabled = await useFeatureFlag("isFontSriEnabled")

  if (!enabled || !tcFontCdnURL) {
    manifest.value = {}
    return manifest.value
  }

  const requestId = nanoid()
  const url = `${tcFontCdnURL}/fonts/sri-manifest.json`

  try {
    // Stale-if-error: a CDN blip should serve the last known manifest rather than drop the
    // integrity attributes, which would look like the feature silently switching itself off.
    manifest.value = await useDataCacheWithStale<FontIntegrityManifest>(
      "fontIntegrityManifest",
      () => $fetch<FontIntegrityManifest>(url),
      { maxAge: MANIFEST_MAX_AGE, staleIfError: dataCacheStaleTtl, requestId, tags: [] },
    )
  }
  catch (error) {
    // Fail open. Rendering without integrity costs the verification; failing hard here
    // would cost the page.
    Logger(requestId).warn(`Could not resolve the font SRI manifest from "${url}" — rendering font stylesheets without integrity`, error)
    manifest.value = {}
  }

  return manifest.value
}
